import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  isLoadingQuiz: true,
  isQuiz: false,
  message: null,
  id: null,
  description: null,
  patient: {},
  procedure: {},
  state: null,
  track: null,
  dasiScore: null,
  mets: null,
  cRisk: null,
  asa: null,
  summary: null,
  numberEvents: null,
  protocol: null,
  scheduledEventCount: null,
  scheduledEventFinishedCount: null,
  finishedProcedureEventsCount: null,
  assessmentEvents: [],
  procedureEvents: [],
  checking: false,
  errorMessage: null,
  schedule: null,
  viewVideoProcedure: false,
  viewPdfProcedure: false,
  isTutorialOpen: false,
  finishedProcedure: null,
  doctorsSelect: {
    data: [],
    isLoading: false,
    message: null,
  },
  isProcedureScheduled: false,
};

const resetState = (state, keys) => {
  keys.forEach((key) => {
    state[key] = initialState[key];
  });
};

export const patientSlice = createSlice({
  name: "patient",
  initialState,
  reducers: {
    onLoadQuiz: (state, { payload }) => {
      state.isLoadingQuiz = false;
      state.isQuiz = true;
      state.id = payload.id;
      state.patient = payload.patient;
      state.procedure = payload.procedure;
      state.state = payload.state;
      state.track = payload?.track;
      state.dasiScore = payload?.dasiScore;
      state.mets = payload?.mets;
      state.cRisk = payload?.cRisk;
      state.asa = payload?.asa;
      state.summary = payload?.summary;
      state.description = payload?.description;
      state.numberEvents = payload?.numberEvents;
      state.protocol = payload?.protocol;
      state.scheduledEventCount = payload?.scheduledEventCount;
      state.scheduledEventFinishedCount = payload?.scheduledEventFinishedCount;
      state.finishedProcedureEventsCount =
        payload?.finishedProcedureEventsCount;
      state.assessmentEvents = payload.assessmentEvents;
      state.procedureEvents = payload.procedureEvents;
      state.finishedProcedure = payload?.finishedProcedure;
    },
    onFailedQuiz: (_, { payload }) => ({
      ...initialState,
      isLoadingQuiz: false,
      errorMessage: payload,
    }),
    onChecking: (state) => {
      state.checking = true;
      state.successMessage = null;
      state.errorMessage = null;
      state.message = null;
    },
    onNotChecking: (state) => {
      state.checking = false;
    },
    onLoadRisk: (state, { payload }) => {
      state.checking = false;
      state.state = payload.state;
      state.track = payload?.track;
      state.dasiScore = payload?.dasiScore;
      state.mets = payload?.mets;
      state.cRisk = payload?.cRisk;
      state.asa = payload?.asa;
      state.summary = payload?.summary;
    },
    onUpdateState: (state, { payload }) => {
      state.state = payload.state;
      state.schedule = null;
    },
    onUpdateScheduleEvent: (state, { payload }) => {
      const { scheduleEvent } = payload;

      state.schedule.eventSchedules = state.schedule.eventSchedules.map(
        (eventSchedule) =>
          eventSchedule.id === scheduleEvent.id ? scheduleEvent : eventSchedule
      );
    },
    onAddScheduledEventCount: (state, { payload }) => {
      state.scheduledEventCount += payload;
    },
    onRemoveScheduledEventCount: (state, { payload }) => {
      state.scheduledEventCount -= payload;
    },
    onViewVideoProcedure: (state) => {
      state.viewVideoProcedure = true;
    },
    onViewPdfProcedure: (state) => {
      state.viewPdfProcedure = true;
    },
    onErrorMessage: (state, { payload }) => {
      state.checking = false;
      state.errorMessage = payload;
      state.successMessage = null;
      state.message = {
        type: "error",
        text: payload,
      };
    },
    onSuccesMessage: (state, { payload }) => {
      state.checking = false;
      state.successMessage = payload;
      state.errorMessage = null;
      state.message = {
        type: "success",
        text: payload,
      };
    },
    onLoadSchedule: (state, { payload }) => {
      state.checking = false;
      state.successMessage = null;
      state.errorMessage = null;
      state.schedule = payload;
    },
    onCleanSchedule: (state) => {
      state.schedule = null;
    },
    openTutorial: (state) => {
      state.isTutorialOpen = true;
    },
    closeTutorial: (state) => {
      state.isTutorialOpen = false;
    },
    onNotViewMedia: (state) => {
      state.viewVideoProcedure = false;
      state.viewPdfProcedure = false;
    },
    addProcedureEvent: (state, { payload }) => {
      state.procedureEvents.unshift(payload);
      state.isProcedureScheduled = true;
    },
    removeProcedureEvent: (state, { payload }) => {
      state.procedureEvents = state.procedureEvents.filter(
        (event) => event.id !== payload
      );
    },
    addValorationEvent: (state, { payload }) => {
      state.assessmentEvents.unshift(payload);
    },
    removeValorationEvent: (state, { payload }) => {
      state.assessmentEvents = state.assessmentEvents.filter(
        (event) => event.id !== payload
      );
    },
    removeScheduledEvent: (state, { payload }) => {
      const { scheduleEvent } = payload;

      state.schedule.eventSchedules = state.schedule.eventSchedules.filter(
        (event) => event.id !== scheduleEvent.id
      );
    },
    loadingDoctorsSelect: (state) => {
      state.doctorsSelect.isLoading = true;
      state.doctorsSelect.message = null;
    },
    loadedDoctorsSelect: (state, { payload }) => {
      state.doctorsSelect.data = payload.doctors;
      state.doctorsSelect.isLoading = false;
      state.doctorsSelect.message = {
        type: "success",
        text: payload.message,
      };
    },
    failedDoctorsSelect: (state, { payload }) => {
      state.doctorsSelect.isLoading = false;
      state.doctorsSelect.message = {
        type: "error",
        text: payload,
      };
    },
    resetProcedureScheduled: (state) => {
      resetState(state, ["isProcedureScheduled"]);
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  onLoadQuiz,
  onFailedQuiz,
  onChecking,
  onNotChecking,
  onLoadRisk,
  onErrorMessage,
  onSuccesMessage,
  onLoadSchedule,
  onCleanSchedule,
  onUpdateState,
  onUpdateScheduleEvent,
  onAddScheduledEventCount,
  onRemoveScheduledEventCount,
  onViewVideoProcedure,
  onViewPdfProcedure,
  openTutorial,
  closeTutorial,
  onNotViewMedia,
  addProcedureEvent,
  removeProcedureEvent,
  addValorationEvent,
  removeValorationEvent,
  removeScheduledEvent,
  loadingDoctorsSelect,
  loadedDoctorsSelect,
  failedDoctorsSelect,
  resetProcedureScheduled,
} = patientSlice.actions;
